import React from "react";
import { ConfigProvider } from "antd";
import { useDashTheme } from "../../hooks/useDashTheme";
import { paper } from "../graphical/viz";

export { paper };

export function sheet(t) {
  return paper(t);
}

export function Sheet({ children, pad = 0, rail, hover, style, onClick, ...rest }) {
  const t = useDashTheme();
  const p = paper(t);
  const [hot, setHot] = React.useState(false);
  const lift = !!(hover || onClick);
  return (
    <div
      onClick={onClick}
      onMouseEnter={lift ? () => setHot(true) : undefined}
      onMouseLeave={lift ? () => setHot(false) : undefined}
      style={{
        position: "relative",
        overflow: "hidden",
        background: p.card,
        border: `1px solid ${hot ? t.c.borderStrong : t.c.border}`,
        borderRadius: t.r.card,
        boxShadow: hot ? t.shadow.raisedHover : t.shadow.raised,
        transform: hot && lift ? "translateY(-2px)" : "none",
        transition: "box-shadow .2s cubic-bezier(.16,1,.3,1), border-color .2s, transform .2s cubic-bezier(.16,1,.3,1)",
        padding: pad,
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
      {...rest}
    >
      {rail ? (
        <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: rail }} />
      ) : null}
      {children}
    </div>
  );
}

export function Paper(props) {
  return <Sheet {...props} />;
}

export function Well({ children, style }) {
  const t = useDashTheme();
  return (
    <div
      style={{
        background: paper(t).well,
        border: `1px solid ${t.c.border}`,
        borderRadius: t.r.well,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Micro({ children, style }) {
  const t = useDashTheme();
  return (
    <div style={{ ...t.font.microLabel, color: t.c.textTertiary, ...style }}>
      {children}
    </div>
  );
}

export function Rule({ title, hint, extra, style }) {
  const t = useDashTheme();
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 12, ...style }}>
      <div style={{ ...t.font.statLabel, color: t.c.primary, flexShrink: 0 }}>{title}</div>
      {hint ? <div style={{ fontSize: 12, color: t.c.textTertiary }}>{hint}</div> : null}
      <span style={{ flex: 1, height: 1, background: `linear-gradient(to right, ${t.c.border}, transparent)` }} />
      {extra}
    </div>
  );
}

export function KpiCell({ label, value, sub, color }) {
  const t = useDashTheme();
  return (
    <Sheet pad="14px 16px">
      <div style={{ ...t.font.statLabel, color: t.c.primary }}>{label}</div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          marginTop: 6,
          letterSpacing: "-0.55px",
          lineHeight: 1,
          fontVariantNumeric: "tabular-nums",
          color: color || t.c.text,
        }}
      >
        {value}
      </div>
      {sub ? <div style={{ marginTop: 6, fontSize: 12, color: t.c.textTertiary, fontWeight: 500 }}>{sub}</div> : null}
    </Sheet>
  );
}

export function Figure({ n, label, color }) {
  return <KpiCell label={label} value={n} color={color} />;
}

export function QuietEmpty({ children }) {
  const t = useDashTheme();
  return (
    <div style={{ padding: "36px 20px", textAlign: "center", fontSize: 13, color: t.c.textTertiary, lineHeight: 1.5 }}>
      {children}
    </div>
  );
}

export function LivingAntd({ children }) {
  const t = useDashTheme();
  const p = paper(t);
  const cfg = React.useMemo(() => ({
    token: {
      colorBgContainer: p.well,
      colorBgElevated: t.c.surface,
      colorFillTertiary: p.well,
      colorBorder: t.c.border,
      colorBorderSecondary: t.c.border,
    },
    components: {
      Table: {
        headerBg: "transparent",
        headerSplitColor: "transparent",
        headerColor: t.c.textTertiary,
        rowHoverBg: t.isDark ? t.c.surfaceHover : "rgba(255,255,255,0.42)",
        rowSelectedBg: t.c.primarySoft,
        cellPaddingBlock: 13,
        cellPaddingInline: 16,
        borderColor: t.c.border,
      },
      Input: { hoverBorderColor: t.c.borderStrong, activeBorderColor: t.c.primary },
      Select: { hoverBorderColor: t.c.borderStrong },
      DatePicker: { hoverBorderColor: t.c.borderStrong, colorBgElevated: t.c.surface },
      Modal: { contentBg: t.c.surface, headerBg: t.c.surface },
    },
  }), [t, p.well]);
  return <ConfigProvider theme={cfg}>{children}</ConfigProvider>;
}

export function BlotterCss() {
  const t = useDashTheme();
  return (
    <style>{`
      .lt-root .ant-form-item-label > label {
        font-size: 10px !important;
        font-weight: 800 !important;
        letter-spacing: 0.08em !important;
        text-transform: uppercase !important;
        height: auto !important;
        color: ${t.c.textTertiary} !important;
      }
      .lt-root .ant-table,
      .lt-root .ant-table-container,
      .lt-root .ant-table-content,
      .lt-root .ant-table-header,
      .lt-root .ant-table-body,
      .lt-root .ant-table-thead > tr > th,
      .lt-root .ant-table-cell {
        background: transparent !important;
      }
      .lt-root .ant-table-thead > tr > th {
        font-size: 10px !important;
        font-weight: 800 !important;
        letter-spacing: 0.08em !important;
        text-transform: uppercase !important;
        border-bottom: 1px solid ${t.c.border} !important;
        padding-top: 10px !important;
        padding-bottom: 10px !important;
      }
      .lt-root .ant-table-tbody > tr > td {
        border-bottom: 1px solid ${t.c.border} !important;
      }
      .lt-root .ant-table-tbody > tr:last-child > td { border-bottom: none !important; }
      .lt-root .ant-table-tbody > tr.ant-table-placeholder > td { border-bottom: none !important; }
      .lt-root .lt-grey-row > td { background: ${t.status.negativeSoft} !important; }
      .lt-root .ant-empty-image { display: none; }
      .lt-root .ant-card { background: ${paper(t).card}; }
    `}</style>
  );
}

export function intakeGrid(min = 132) {
  return {
    display: "grid",
    gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
    gap: 10,
    alignItems: "end",
  };
}
